import {
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import ScheduleDetailHeader from "../../../../../domains/jobs/components/shared/schedule-detail-header/schedule-detail-header.component";
import { Bq9DataService } from "../services/bq9-data.service";
import { UiToastService } from "../../../../../shared/components/toast/ui-toast.service";
import { ExtendedRouter } from "../../../../../shared/services/extended-router.service";
import { ActivatedRoute } from "@angular/router";
import { ButtonModule } from "primeng/button";
import {
  ImageType,
  JobSignature,
  Media,
  PostCompleteStatus,
  SyncableStatus,
} from "../../../../../core/database/db";
import {
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  FormControl,
} from "@angular/forms";
import { CheckboxModule } from "primeng/checkbox";
import { v4 as uuidv4 } from "uuid";
import { DataUpdatedFadeDirective } from "../../../../../shared/directives/data-updated-fade.directive";
import { MessageModule } from "primeng/message";
import {
  ImageCompressionPipelineService,
  MediaForCompression,
} from "../../../../../domains/media/services/image-compression-pipeline.service";
import { BlobService } from "../../../../../shared/services/blob.service";
import { DomSanitizer } from "@angular/platform-browser";
import { ImagesModalComponent } from "../../../../../domains/media/components/images-modal/images-modal.component";
import { Dialog } from "@angular/cdk/dialog";
import { BqaImageItem } from "../../../../../domains/media/components/images-modal/bqa-image-item";
import { InputErrorComponent } from "../../../../../shared/components/input-error/input-error.component";
import { SkipSignatureBottomSheetComponent, SkipSignatureData } from "./components/skip-signature-bottom-sheet/skip-signature-bottom-sheet.component";

@Component({
  selector: "app-bq9-review-and-sign",
  imports: [
    ScheduleDetailHeader,
    ButtonModule,
    ReactiveFormsModule,
    CheckboxModule,
    MessageModule,
    InputErrorComponent,
    SkipSignatureBottomSheetComponent,
    DataUpdatedFadeDirective,
  ],
  templateUrl: "./bq9-review-and-sign.component.html",
  styleUrl: "./bq9-review-and-sign.component.scss",
})
export class Bq9ReviewAndSignComponent implements OnInit {
  private bq9DataService = inject(Bq9DataService);
  private toastService = inject(UiToastService);
  private router = inject(ExtendedRouter);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private blobService = inject(BlobService);
  private sanitizer = inject(DomSanitizer);
  private dialog: Dialog = inject(Dialog);
  public imageCompressionPipelineService = inject(
    ImageCompressionPipelineService,
  );

  private objectUrls = new Set<string>();

  subtitle = "";
  dataLoaded = false;
  step = signal<number>(1);
  showValidationErrors = signal<boolean>(false);
  photoAgreementRequired = signal<boolean>(false);
  showSkipSheet = signal<boolean>(false);
  didSkipSignatures = signal<boolean>(false);
  skipData: SkipSignatureData | null = null;
  readonly bq8HomeownerEmails = computed(() => this.bq9DataService.bq8HomeownerEmails());
  readonly isRefreshingInBackground = computed(() => this.bq9DataService.isRefreshingInBackground());
  readonly dataUpdatedPulse = computed(() => this.bq9DataService.dataUpdatedPulse());
  expandedIssues = signal<Set<string>>(new Set());

  readonly ISSUE_TEXT_READ_MORE_THRESHOLD = 120;

  signForm = this.fb.group({
    agreed: [false, Validators.requiredTrue],
    h1Signature: [""],
    h1Email: ["", Validators.email],
    h2Signature: [""],
    h2Email: ["", Validators.email],
  });

  photoForm = this.fb.group({
    agreed: [false],
    photos: new FormControl<Media[]>([]),
  });

  @ViewChild("cameraInput") cameraInput!: ElementRef<HTMLInputElement>;
  @ViewChild("galleryInput") galleryInput!: ElementRef<HTMLInputElement>;

  images = signal<Media[]>([]);

  allImagesRaw = computed(() => this.images().filter(im => !im.deleted));

  allImages = computed(() =>
    this.allImagesRaw().map(img => {
      let azureBlobUri = "";
      if (img.base64Image) {
        azureBlobUri = this.createObjectUrlAndTrack(
          this.blobService.base64ToBlob(img.base64Image),
        );
      } else if (img.resourceUri) {
        azureBlobUri = img.resourceUri;
      }
      return azureBlobUri
        ? this.sanitizer.bypassSecurityTrustUrl(azureBlobUri)
        : undefined;
    }),
  );

  galleryItems = computed(() =>
    this.allImagesRaw()
      .filter(
        image => (image.base64Image || image.resourceUri) && !image.deleted,
      )
      .map(image => {
        let azureBlobUri = "";
        if (image.base64Image) {
          azureBlobUri = this.createObjectUrlAndTrack(
            this.blobService.base64ToBlob(image.base64Image),
          );
        } else if (image.resourceUri) {
          azureBlobUri = image.resourceUri;
        }
        return new BqaImageItem({
          azureBlobUri: azureBlobUri || "",
          mediaIdentifier: image.fileIdentifier,
          isThumbnailOnly: image.isThumbnailOnly || false,
        });
      }),
  );

  readonly stepHeader = computed(() => {
    switch (this.step()) {
      case 2:
        return "Homeowner Photo";
      case 1:
      default:
        return "Review & Sign";
    }
  });

  readonly stepDescription = computed(() => {
    switch (this.step()) {
      case 2:
        return "Optional. Skip if the customer does not want a photo taken.";
      case 1:
      default:
        return "Here is the summary from today's walkthrough.";
    }
  });

  readonly agreementTitle = computed(() => {
    switch (this.step()) {
      case 2:
        return "I agree Pulte can use this photo.";
      case 1:
        if (
          this.seasonalIssues().length > 0 ||
          this.followUpIssues().length > 0
        ) {
          return "Yes, I am ready to go to closing.";
        }
        return "Yes, my home is 100% complete.";
      default:
        return "Yes, my home is 100% complete.";
    }
  });

  readonly agreementDescription = computed(() => {
    switch (this.step()) {
      case 2:
        return "By checking this box, you grant Pulte permission to share this photo for marketing, social media, and internal company communications.";
      case 1:
        if (
          this.seasonalIssues().length > 0 ||
          this.followUpIssues().length > 0
        ) {
          return "My home is complete to my satisfaction, and I acknowledge that the open items listed above will be resolved per the timeline agreed with Pulte.";
        }
        return "Everything is finished to my satisfaction and I am ready to close.";
      default:
        return "Everything is finished to my satisfaction and I am ready to close.";
    }
  });

  readonly issues = this.bq9DataService.jobIssues;

  readonly activeIssues = computed(() => {
    return this.issues()?.filter(issue => issue.deleted !== 1) || [];
  });

  readonly hasNoIssues = computed(() => this.activeIssues().length === 0);

  readonly confirmedByHomeownerIssues = computed(() => {
    return this.activeIssues().filter(issue => issue.isConfirmedByHomeowner);
  });

  readonly seasonalIssues = computed(() => {
    return this.activeIssues().filter(
      issue => issue.postCompleteStatus === PostCompleteStatus.Seasonal,
    );
  });

  readonly followUpIssues = computed(() => {
    return this.activeIssues().filter(
      issue => issue.postCompleteStatus === PostCompleteStatus.FollowUp,
    );
  });

  isPairValid(
    sigCtrl: "h1Signature" | "h2Signature",
    emailCtrl: "h1Email" | "h2Email",
  ): boolean {
    const sig = this.signForm.value[sigCtrl]?.trim();
    const email = this.signForm.value[emailCtrl]?.trim();
    const isEmailValid = this.signForm.get(emailCtrl)?.valid;
    return !!sig && !!email && !!isEmailValid;
  }

  isPairPartial(
    sigCtrl: "h1Signature" | "h2Signature",
    emailCtrl: "h1Email" | "h2Email",
  ): boolean {
    const sig = this.signForm.value[sigCtrl]?.trim();
    const email = this.signForm.value[emailCtrl]?.trim();

    if (!sig && !email) {
      return false;
    }
    if (this.isPairValid(sigCtrl, emailCtrl)) {
      return false;
    }
    return true;
  }

  get hasAtLeastOneValidPair(): boolean {
    return (
      this.isPairValid("h1Signature", "h1Email") ||
      this.isPairValid("h2Signature", "h2Email")
    );
  }

  get h1SigError(): boolean {
    if (!this.showValidationErrors() || this.didSkipSignatures()) {
      return false;
    }
    return (
      (!this.hasAtLeastOneValidPair ||
        this.isPairPartial("h1Signature", "h1Email")) &&
      !this.signForm.value.h1Signature?.trim()
    );
  }

  get h1EmailError(): boolean {
    if (!this.showValidationErrors() || this.didSkipSignatures()) {
      return false;
    }
    return (
      (!this.hasAtLeastOneValidPair ||
        this.isPairPartial("h1Signature", "h1Email")) &&
      (!this.signForm.value.h1Email?.trim() ||
        !!this.signForm.get("h1Email")?.invalid)
    );
  }

  get h2SigError(): boolean {
    if (!this.showValidationErrors() || this.didSkipSignatures()) {
      return false;
    }
    return (
      this.isPairPartial("h2Signature", "h2Email") &&
      !this.signForm.value.h2Signature?.trim()
    );
  }

  get h2EmailError(): boolean {
    if (!this.showValidationErrors() || this.didSkipSignatures()) {
      return false;
    }
    return (
      this.isPairPartial("h2Signature", "h2Email") &&
      (!this.signForm.value.h2Email?.trim() ||
        !!this.signForm.get("h2Email")?.invalid)
    );
  }

  ngOnInit(): void {
    const lot = this.bq9DataService.lot();

    const hasData = !!lot && !!this.issues();
    if (hasData === false) {
      this.toastService.error(
        "Error loading job data for BQ9. Please try again.",
      );
      this.goBack();
      return;
    }

    if (lot) {
      this.subtitle = `Lot ${lot.lotBlock} - ${lot.streetAddress}`;
    }

    const bq8Emails = this.bq8HomeownerEmails();
    this.signForm.patchValue({
      h1Email: bq8Emails[0] ?? "",
      h2Email: bq8Emails[1] ?? "",
    });

    this.dataLoaded = true;
  }

  goBack(): void {
    this.router.navigateWithPersistedState(["../"], { relativeTo: this.route });
  }

  isIssueExpanded(identifier: string): boolean {
    return this.expandedIssues().has(identifier);
  }

  toggleIssueText(identifier: string): void {
    this.expandedIssues.update(set => {
      const next = new Set(set);
      if (next.has(identifier)) {
        next.delete(identifier);
      } else {
        next.add(identifier);
      }
      return next;
    });
  }

  goToStepOne(): void {
    this.step.set(1);
  }

  goToStepTwo(): void {
    this.showValidationErrors.set(true);

    const isAgreed = this.signForm.get("agreed")?.valid;
    const noPartials =
      !this.isPairPartial("h1Signature", "h1Email") &&
      !this.isPairPartial("h2Signature", "h2Email");
    const signaturesValid = this.hasAtLeastOneValidPair && noPartials;
    const skipValid = this.didSkipSignatures();

    if (isAgreed && (signaturesValid || skipValid)) {
      this.showValidationErrors.set(false);
      this.step.set(2);
    }
  }

  openSkipSheet(): void {
    this.showSkipSheet.set(true);
  }

  onSkipConfirmed(data: SkipSignatureData): void {
    this.skipData = data;
    this.didSkipSignatures.set(true);
    this.signForm.patchValue({ h1Signature: "", h1Email: "", h2Signature: "", h2Email: "" });
    this.submit();
  }

  onSkipCleared(): void {
    this.didSkipSignatures.set(false);
    this.skipData = null;
  }

  async handleImage(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = [input.files[0]];
      const mediaRecords = await this.imageCompressionPipelineService.encode(
        files,
        "",
        ImageType.HomeownerSignature,
      );
      this.images.set(mediaRecords);
      this.photoForm
        .get("photos")!
        .setValue(this.allImagesRaw(), { emitEvent: false });
      input.value = "";
      this.imageCompressionPipelineService.compress(
        mediaRecords,
        async compressedMediaRecords => {
          this.images.update(existing =>
            existing.map(originalImage => {
              const compressedImage = compressedMediaRecords.find(
                m => m.fileIdentifier === originalImage.fileIdentifier,
              );
              if (compressedImage) {
                const { file: _file, ...media } =
                  compressedImage as MediaForCompression;
                return { ...originalImage, ...media } as Media;
              }
              return originalImage;
            }),
          );
        },
      );
    }
  }

  async submit(): Promise<void> {
    const jobId = this.bq9DataService.job()?.id;

    if (!jobId) {
      return;
    }

    const hasPhoto = this.images().length > 0;
    const isAgreed = this.photoForm.get("agreed")?.value === true;

    const now = new Date();
    const signatures: JobSignature[] = [];

    if (this.didSkipSignatures() && this.skipData) {
      const { h1Email, h2Email, skipType, refusalReason } = this.skipData;
      if (h1Email) {
        signatures.push({
          jobId,
          identifier: uuidv4(),
          signerName: "",
          signerEmail: h1Email,
          signatureSkipType: skipType,
          signatureSkipReason: refusalReason,
          updatedAt: now,
          syncStatus: SyncableStatus.Pending,
        });
      }
      if (h2Email) {
        signatures.push({
          jobId,
          identifier: uuidv4(),
          signerName: "",
          signerEmail: h2Email,
          signatureSkipType: skipType,
          signatureSkipReason: refusalReason,
          updatedAt: now,
          syncStatus: SyncableStatus.Pending,
        });
      }
    } else {
      const signForm = this.signForm.getRawValue();
      if (signForm.h1Signature && signForm.h1Email) {
        signatures.push({
          jobId,
          identifier: uuidv4(),
          signerName: signForm.h1Signature,
          signerEmail: signForm.h1Email,
          signedAt: now,
          updatedAt: now,
          syncStatus: SyncableStatus.Pending,
        });
      }
      if (signForm.h2Signature && signForm.h2Email) {
        signatures.push({
          jobId,
          identifier: uuidv4(),
          signerName: signForm.h2Signature,
          signerEmail: signForm.h2Email,
          signedAt: now,
          updatedAt: now,
          syncStatus: SyncableStatus.Pending,
        });
      }
    }

    if (signatures.length > 0) {
      if (hasPhoto && !this.didSkipSignatures()) {
        await this.imageCompressionPipelineService.waitForCompletion();
        const mediaToSave: Media[] = this.images().map(image => {
          const { file: _file, ...media } = image as MediaForCompression;
          media.isApprovedToUse = isAgreed;
          return media;
        });

        mediaToSave[0].parentIdentifier = signatures[0].identifier;

        await this.bq9DataService.saveSignatures(signatures, mediaToSave[0]);
      } else {
        await this.bq9DataService.saveSignatures(signatures);
      }

      this.router.navigateWithPersistedState(["../submission"], {
        relativeTo: this.route,
      });
    }
  }

  openImagesModal(index: number): void {
    const dialogRef = this.dialog.open<string>(ImagesModalComponent, {
      data: {
        images: this.galleryItems(),
        index,
        isIssue: false,
        onDeleted: (fileIdentifier: string) =>
          this.handleImageDeleted(fileIdentifier),
        backdropClass: "images-modal-backdrop",
      },
    });
    dialogRef.closed.subscribe(_ => { });
  }

  handleImageDeleted(fileIdentifier: string): void {
    this.images.update(existing =>
      existing.filter(img => img.fileIdentifier !== fileIdentifier),
    );
    this.photoForm
      .get("photos")!
      .setValue(this.allImagesRaw(), { emitEvent: false });
  }

  triggerCamera(): void {
    this.cameraInput.nativeElement.click();
  }

  triggerGallery(): void {
    this.galleryInput.nativeElement.click();
  }

  private createObjectUrlAndTrack(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.objectUrls.add(url);
    return url;
  }
}
